import { useMemo, useState } from 'react';

const empty = {};

type UseKeyValueStateControlledProps<T = any> = {
  controller: {
    state?: Record<string, T>;
    setState: (
      updater: (state: Record<string, T>) => Record<string, T>,
    ) => void;
  };
};

export function useKeyValueStateControlled<T = any>(
  props: UseKeyValueStateControlledProps<T>,
) {
  const { setState } = props.controller;

  const state: Record<string, T> = props.controller.state || empty;

  const set = (key: string, value: T) => {
    setState((prevState) => Object.assign({}, prevState, { [key]: value }));
  };

  const update = (key: string, value: Partial<T>) => {
    setState((prevState) => {
      const updated = Object.assign({}, prevState[key], value);
      return Object.assign({}, prevState, { [key]: updated });
    });
  };

  const remove = (key: string) => {
    setState((prevState) => {
      if (!(key in prevState)) return prevState;
      const { [key]: _, ...rest } = prevState;
      return rest;
    });
  };

  const clear = () => setState(() => empty);

  const values = useMemo(() => Object.values(state), [state]);

  return {
    set,
    update,
    values,
    clear,
    remove,
  };
}

function useKeyValueState<T = any>(init?: Record<string, T>) {
  const [state, setState] = useState(init || empty);
  return useKeyValueStateControlled<T>({ controller: { state, setState } });
}

export default useKeyValueState;
